import {
  PrimaryButton, Spacing, commitWorkflowStepByName, requestResourceByName, useScope, useScopedSelector,
} from 'ia-react-core';
import { ConfirmationPage, redirectTo, useMarkPageLoaded } from 'omni-package-react-core';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MfaActionBar from '../../components/MfaActionBar';
import { MfaOnboardingResourceInfo } from '../../../../components/MfaOnboardingResource';
import { getIdentityManagementApiRoot } from '../../../../utils/getIdentityManagementApiRoot';
import { MfaValidationPageState } from '../../../../interfaces/MfaValidationPageState';
import { MfaOnboarding } from '../../../../interfaces/MfaOnboarding';
import PostFactors from './PostFactors';
import AsyncPostFactors from './AsyncPostFactors';

export interface MfaConfirmationStepProps { }

const MfaConfirmationStep: FC<MfaConfirmationStepProps> = () => {
  const { t } = useTranslation('MfaValidationPage');
  const { getScopedState } = useScope();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const workflow = getScopedState((state: MfaValidationPageState) => state?.values?.mfa);
  const challenges = useScopedSelector(({ data }: MfaValidationPageState) => data.informations?.challenges);
  const mfaOnboarding: MfaOnboarding = useScopedSelector(MfaOnboardingResourceInfo.dataDst);

  const smsChallenge = challenges?.find((challenge) => challenge.type === 'sms');
  const emailChallenge = challenges?.find((challenge) => challenge.type === 'email');
  // useMemo prevents the Container from loading twice
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isCompleted = useMemo(() => mfaOnboarding?.data?.onboardingStatus === 'completed', []);
  const startPath = workflow?.password?.hasConfirmedPassword ? '/profile' : '/';
  const Container = isCompleted ? PostFactors : AsyncPostFactors;

  const handleConfirmFactorCalled = useCallback(() => {
    dispatch(requestResourceByName(MfaOnboardingResourceInfo.name));
    dispatch(commitWorkflowStepByName('workflows.mfa', 'confirmation', { isConfirmed: true }));
  }, [dispatch]);

  const handleClick = useCallback(() => {
    redirectTo(startPath, navigate, { pageView: true }, { replace: true });
  }, [navigate, startPath]);

  useMarkPageLoaded();

  return (
    <Container
      url={`${getIdentityManagementApiRoot()}/factors`}
      emailChallenge={emailChallenge}
      smsChallenge={smsChallenge}
      startPath={startPath}
      onConfirmFactorCalled={handleConfirmFactorCalled}
    >
      <ConfirmationPage
        title={t('steps.confirmation.title')}
        hideCloseButton
      >
        <Spacing m-b={20} data-testid="mfaDescription">{t('steps.confirmation.description')}</Spacing>
        <Spacing m-b={12}>
          <MfaActionBar>
            <PrimaryButton onClick={handleClick}>
              {t('steps.confirmation.confirmButtonText')}
            </PrimaryButton>
          </MfaActionBar>
        </Spacing>
      </ConfirmationPage>
    </Container>
  );
};

export default MfaConfirmationStep;
